<template>
  <div
    class="search-dropdown"
    v-click-outside="function() { toggleDropdown(false) }">
    <div
      v-if="selectedIds.length > 0"
      class="search-dropdown-clear"
      @click="clearSelection">×</div>
    <input
      class="search-dropdown-input"
      type="text"
      v-model="filter"
      @focus="toggleDropdown(true)"
      @keydown.enter.prevent="focused !== -1 && selectItem(filteredItems[focused].id)"
      @keydown.up.prevent="toggleDropdown(true), focus(focused - 1, true)"
      @keydown.down.prevent="toggleDropdown(true), focus(focused + 1, true)"
      @keydown.tab="toggleDropdown(false)"
      @input="toggleDropdown(true)"
      :placeholder="adjustedPlaceholder">
    <ul
      :class="['search-dropdown-options', { visible: this.active }]"
      @mouseleave="focus(-1)">
      <template v-for="item, index in filteredItems">
        <li
          v-if="item.groupLabel"
          class="search-dropdown-group-label">
          {{ item.groupLabel }}
        </li>
        <li
          :class="{ selected : item.selected, focused : focused === index }"
          @click="selectItem(item.id)"
          @mouseenter="focus(index)">
          <div class="search-dropdown-label">{{ item.label }}</div>
          <div v-if="item.count">{{ item.count }}</div>
        </li>
      </template>
    </ul>
  </div>
</template>

<script>
const ITEM_HEIGHT = 23;
const ITEM_COUNT = 10;
export default {
  data: () => ({
    filter: '',
    active: false,
    focused: -1,
  }),
  watch: {
    filter() {
      this.focus(this.focused);
    },
  },
  computed: {
    filteredItems() {
      const filterAndSort = items => items
        .filter(item =>
          item.selected || item.label.toLowerCase().indexOf(this.filter.toLowerCase()) > -1,
        )
        .map((item, index) => {
          // eslint-disable-next-line no-param-reassign
          item.sortIndex = index;
          return item;
        })
        .sort((a, b) => {
          if (!this.single) {
            if (this.alphabetise && (Boolean(a.selected) === Boolean(b.selected))) {
              return a.label.localeCompare(b.label, 'sl');
            }
            if (a.selected && !b.selected) return -1;
            else if (!a.selected && b.selected) return 1;
          }
          if (a.sortIndex < b.sortIndex) return -1;
          else if (a.sortIndex > b.sortIndex) return 1;
          return 0;
        })
        .map((item) => {
          // eslint-disable-next-line no-param-reassign
          delete item.sortIndex;
          return item;
        });
      if (this.groups) {
        return this.groups
          .map((group) => {
            const itemsFromGroup = filterAndSort(this.items.filter(
              item => group.items.indexOf(item.id) > -1),
            );
            itemsFromGroup.forEach((item, index) => {
              // eslint-disable-next-line no-param-reassign
              item.groupLabel = index === 0 ? group.label : null;
            });
            return itemsFromGroup;
          })
          .reduce((a, b) => a.concat(b), []);
      }
      return filterAndSort(this.items);
    },
    selectedIds() {
      return this.filteredItems
        .filter(item => item.selected)
        .map(item => item.id);
    },
    adjustedPlaceholder() {
      if (!this.single) {
        return this.placeholder;
      }
      const selectedItem = this.filteredItems.filter(item => item.selected)[0];
      return selectedItem ? selectedItem.label : this.placeholder;
    },
  },
  directives: {
    clickOutside: {
      bind(el, binding) {
        const handler = (e) => {
          if (!el.contains(e.target) && el !== e.target) {
            binding.value(e);
          }
        };
        el.vueClickOutside = handler; // eslint-disable-line no-param-reassign
        document.addEventListener('click', handler);
      },
      unbind(el) {
        document.removeEventListener('click', el.vueClickOutside);
        el.vueClickOutside = null; // eslint-disable-line no-param-reassign
      },
    },
  },
  props: {
    items: { type: Array, required: true },
    placeholder: { type: String, required: true },
    groups: { type: Array, required: false },
    alphabetise: { type: Boolean, required: false, default: true },
    single: { type: Boolean, required: false },
  },
  methods: {
    selectItem(selectedItemId) {
      if (this.single) {
        this.clearSelection();
        this.toggleItem(selectedItemId);
        this.toggleDropdown(false);
        this.$emit('change', this.items.find(item => item.id === selectedItemId));
      } else {
        this.toggleItem(selectedItemId);
      }
    },
    toggleItem(itemId) {
      const clickedItem = this.items.filter(item => item.id === itemId)[0];
      this.$set(clickedItem, 'selected', !clickedItem.selected);
    },
    toggleDropdown(state) {
      if (state === false) {
        this.filter = '';
      }
      this.active = state;
    },
    clearSelection() {
      this.selectedIds.forEach(this.toggleItem);
    },
    focus(index, withKeyboard) {
      this.focused = Math.max(Math.min(this.filteredItems.length - 1, index), -1);
      if (!withKeyboard) return;
      const additionalOffset = this.filteredItems.slice(0, this.focused + 1)
        .map(item => (item.groupLabel ? 1 : 0))
        .reduce((a, b) => a + b, 0);
      const optionListEl = this.$el.lastChild;
      const focusedPosition = (this.focused + additionalOffset) * ITEM_HEIGHT;
      if (focusedPosition < optionListEl.scrollTop) {
        optionListEl.scrollTop = focusedPosition;
      } else if (focusedPosition > optionListEl.scrollTop + ((ITEM_COUNT - 1) * ITEM_HEIGHT)) {
        optionListEl.scrollTop = focusedPosition - ((ITEM_COUNT - 1) * ITEM_HEIGHT);
      }
    },
  },
};
</script>

<style lang="scss">
@import '../assets/constants';

.search-dropdown {
  border: 1px solid $blue-main;
  position: relative;
}

.search-dropdown::after {
  content: "";
  width: 20px;
  height: 20px;
  pointer-events: none;
  position: absolute;
  top: calc(50% - 10px);
  right: 10px;
  @include background-svg("../assets/images/puscica_nazaj.svg", $blue-main);
  background-repeat: no-repeat;
  background-position: center center;
  background-size: auto 20px;
  transform: rotate(270deg);
}

.search-dropdown-clear {
  width: 20px;
  height: 20px;
  font-size: 25px;
  line-height: 20px;
  cursor: pointer;
  color: $blue-main;
  text-align: center;
  position: absolute;
  top: calc(50% - 10px);
  right: 36px;
}

.search-dropdown-input {
  background: #fff;
  border: none;
  font-size: 0.875rem;
  line-height: 17px;
  outline: none;
  padding: 8px 56px 8px 12px;
  width: 100%;

  &::placeholder {
    color: #000;
  }
}

.search-dropdown-options {
  background: #ffffff;
  border-left: 1px solid $blue-main;
  border-bottom: 1px solid $blue-main;
  border-right: 1px solid $blue-main;
  display: none;
  position: absolute;
  left: -1px;
  top: 34px;
  width: calc(100% + 2px);
  max-height: 230px;
  list-style: none;
  margin: 0;
  padding: 0;
  overflow-y: auto;
  z-index: 3;
}

.search-dropdown-options.visible {
  display: block;
}

.search-dropdown-options li {
  cursor: pointer;
  display: flex;
  font-size: 0.875rem;
  height: 23px;
  line-height: 23px;
  padding: 0 12px;
  position: relative;
  user-select: none;
}

.search-dropdown-options li.search-dropdown-group-label {
  background: #ededed;
  cursor: default;
  text-transform: uppercase;
}

.search-dropdown-options li.focused {
  background: #f3f7f9;
}

.search-dropdown-options li.selected {
  background-color: $blue-main;
  color: #fff;
}

.search-dropdown-options li.selected.focused {
  background-color: #e7eef3;
}

.search-dropdown-options li:not(:last-of-type):not(.selected)::after {
  content: "";
  background-color: #e7eef3;
  height: 1px;
  width: calc(100% - 24px);
  position: absolute;
  left: 12px;
  bottom: 0;
}

.search-dropdown-options li .search-dropdown-label {
  flex: 1;
  margin-right: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.search-dropdown ::-webkit-scrollbar {
  width: 4px;
  height: 4px;
}

.search-dropdown ::-webkit-scrollbar-track {
  background: #b0c8d8;
  border-radius: 0;
}

.search-dropdown ::-webkit-scrollbar-thumb {
  background: #5a84a0;
  border-radius: 0;
}
</style>



// WEBPACK FOOTER //
// src/components/SearchDropdown.vue